const hasOwn = (object, key) =>
  Object.prototype.hasOwnProperty.call(object, key);

const firstDefined = (data, keys) => {
  const key = keys.find(candidate => hasOwn(data, candidate));
  return key === undefined ? undefined : data[key];
};

const stringifyScalarId = value => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'bigint') {
    return String(value);
  }
  // Preserve malformed imported values. The runtime rejects them fail-closed;
  // silently turning an object into an empty filter would make it match all.
  return value;
};

const stringifyIdList = value =>
  Array.isArray(value) ? value.map(stringifyScalarId) : value;

export class FlowContractConflictError extends Error {
  constructor(canonicalKey, keys) {
    super(`Conflicting flow values for ${canonicalKey}: ${keys.join(', ')}`);
    this.name = 'FlowContractConflictError';
    this.canonicalKey = canonicalKey;
  }
}

const equivalentValue = (left, right) => {
  if (Object.is(left, right)) return true;
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
};

const assignCanonical = (data, canonicalKey, aliasKeys, transform) => {
  const keys = [canonicalKey, ...aliasKeys];
  const definedKeys = keys.filter(key => hasOwn(data, key));
  const normalizedValues = definedKeys.map(key =>
    transform ? transform(data[key]) : data[key]
  );
  if (
    normalizedValues.length > 1 &&
    normalizedValues.some(value => !equivalentValue(value, normalizedValues[0]))
  ) {
    throw new FlowContractConflictError(canonicalKey, definedKeys);
  }
  const value = normalizedValues[0];

  aliasKeys.forEach(key => delete data[key]);
  if (definedKeys.length) data[canonicalKey] = value;
};

export const LEGACY_CONDITION_FIELD_ALIASES = Object.freeze({
  'contact.phone': 'contact.phone_number',
  'conversation.assignee': 'conversation.assignee_id',
  'conversation.team': 'conversation.team_id',
  'conversation.channel': 'inbox.channel_type',
  'conversation.label': 'conversation.label_list',
});

export const normalizeConditionField = field =>
  typeof field === 'string'
    ? LEGACY_CONDITION_FIELD_ALIASES[field] || field
    : field;

const PIPELINE_MOVE_EVENTS = new Set([
  'pipeline_card_stage_changed',
  'pipeline_item_moved',
]);
const PIPELINE_MOVE_SUBTYPES = new Set(['stage_changed', 'item_moved']);
const GENERIC_TRIGGER_TYPES = new Set(['', 'event', 'trigger']);

const EVENT_TO_SUBTYPE = Object.freeze({
  pipeline_item_created: 'item_created',
  pipeline_card_stage_changed: 'item_moved',
  pipeline_item_moved: 'item_moved',
  pipeline_card_won: 'item_won',
  first_reply_created: 'first_reply_sent',
});

export const normalizeTriggerSubtype = rawData => {
  if (!rawData || typeof rawData !== 'object' || Array.isArray(rawData)) {
    return '';
  }

  const type = rawData.type || '';
  const eventType = firstDefined(rawData, ['event_type', 'eventType']) || '';
  if (PIPELINE_MOVE_SUBTYPES.has(type) || PIPELINE_MOVE_EVENTS.has(eventType)) {
    return 'item_moved';
  }
  if (type && !GENERIC_TRIGGER_TYPES.has(type)) return type;
  return EVENT_TO_SUBTYPE[eventType] || eventType || type;
};

const normalizeTriggerEvent = (subtype, eventType) => {
  if (
    PIPELINE_MOVE_SUBTYPES.has(subtype) ||
    PIPELINE_MOVE_EVENTS.has(eventType)
  ) {
    return 'pipeline_item_moved';
  }
  return eventType;
};

// Persist one canonical, snake_case trigger contract. The editor and importer
// still dual-read the legacy aliases, but save/validate/import never emit two
// conflicting representations of the same filter.
export const normalizeTriggerFilterData = rawData => {
  if (!rawData || typeof rawData !== 'object' || Array.isArray(rawData)) {
    return rawData;
  }

  const data = { ...rawData };
  const subtype = normalizeTriggerSubtype(data);
  const eventKeys = ['event_type', 'eventType'].filter(key =>
    hasOwn(data, key)
  );
  const normalizedEvents = eventKeys.map(key =>
    normalizeTriggerEvent(subtype, data[key])
  );
  if (
    normalizedEvents.length > 1 &&
    normalizedEvents.some(event => !equivalentValue(event, normalizedEvents[0]))
  ) {
    throw new FlowContractConflictError('event_type', eventKeys);
  }
  const rawEventType = firstDefined(data, ['event_type', 'eventType']);
  const eventType = normalizeTriggerEvent(subtype, rawEventType);

  delete data.eventType;
  if (subtype) data.type = subtype;
  if (eventType !== undefined && eventType !== '') {
    data.event_type = eventType;
  }

  assignCanonical(
    data,
    'pipeline_id',
    ['pipelineId', 'funnel_id'],
    stringifyScalarId
  );
  assignCanonical(
    data,
    'from_stage_id',
    ['fromColumn', 'stage_from'],
    stringifyScalarId
  );
  assignCanonical(
    data,
    'to_stage_id',
    ['toColumn', 'stage_to'],
    stringifyScalarId
  );
  assignCanonical(data, 'pattern', ['keyword']);
  assignCanonical(data, 'inbox_ids', ['inboxIds'], stringifyIdList);
  assignCanonical(data, 'channel_type', ['channelType']);
  assignCanonical(data, 'message_type', ['messageType']);
  assignCanonical(data, 'only_first_message', ['onlyFirstMessage']);
  assignCanonical(data, 'stage_ids', ['stageIds'], stringifyIdList);
  assignCanonical(data, 'from_status', ['fromStatus']);
  assignCanonical(data, 'to_status', ['toStatus']);
  assignCanonical(data, 'label_action', ['labelAction']);
  assignCanonical(
    data,
    'filter_label_id',
    ['filterLabelId'],
    stringifyScalarId
  );
  assignCanonical(data, 'added_label_ids', ['addedLabelIds'], stringifyIdList);
  assignCanonical(data, 'added_labels', ['addedLabels']);
  assignCanonical(data, 'removed_labels', ['removedLabels']);

  return data;
};

const normalizeConditionData = rawData => {
  if (!rawData || typeof rawData !== 'object' || Array.isArray(rawData)) {
    return rawData;
  }

  const data = { ...rawData };
  if (hasOwn(data, 'field')) {
    data.field = normalizeConditionField(data.field);
  }
  if (Array.isArray(data.conditions)) {
    data.conditions = data.conditions.map(normalizeConditionData);
  }
  return data;
};

export const normalizeAutomationFlow = flow => {
  if (!flow || typeof flow !== 'object' || !Array.isArray(flow.nodes)) {
    return flow;
  }

  return {
    ...flow,
    nodes: flow.nodes.map(node => {
      if (node?.type === 'trigger') {
        return { ...node, data: normalizeTriggerFilterData(node.data) };
      }
      if (node?.type === 'condition') {
        return { ...node, data: normalizeConditionData(node.data) };
      }
      return node;
    }),
  };
};

// Kept as a compatibility export for callers introduced during the rolling
// migration. It now delegates to the complete flow boundary.
export const normalizeFlowTriggerFilters = normalizeAutomationFlow;
